import ImageWebpComponent from "@/components/ImageWebpComponent"
import { useDebounce } from "@/hooks"
import { useAuth } from "@/providers/authContext"
import { useGetHistoryTaskInfiniteQuery } from "@/queries/task/useGetTaskInfiniteQuery"
import { useWorkspaceSearchHistory } from "@/stores"
import useCustomRouter from "@/hooks/useCustomRouter"
import { ReactNode, useMemo, useState } from "react"
import SearchBar from "../../FoldersV2/SearchBar"
import { googleAnalytics } from "@/lib/gtag"
import { cn } from "@/lib/utils"
import { CheckCircleIcon } from "@/components/shared/icons"

interface RecipeSearchProps {
  sendRecipe: string
  children?: ReactNode
  selectionMode: boolean
  toggleSelectionMode: (open?: boolean) => void
  className?: string
}

const RecipeSearch = ({ sendRecipe, children, className, selectionMode, toggleSelectionMode }: RecipeSearchProps) => {
  const router = useCustomRouter()
  const { user, loading } = useAuth()
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounce(search, 350)

  const [addHistory, removeHistory] = useWorkspaceSearchHistory(s => [s.addHistory, s.removeHistory])
  const history = useWorkspaceSearchHistory(s =>
    s.history.filter(item => item.uid === user?.uid && item.location === "/workspace/tools/recipes"),
  )

  const handleConfirmSearch = (search: string) => {
    router.push(
      {
        query: { ...router.query, search },
      },
      undefined,
      { shallow: true },
    )

    googleAnalytics.event({
      action: "click",
      category: "recipe_search",
      label: "search",
      params: {
        search: search,
      },
    })

    if (search) {
      addHistory({
        uid: user?.uid ?? "",
        location: "/workspace/tools/recipes",
        text: search,
      })
    }
  }

  const handleClearSearchHistory = (text: string) => {
    removeHistory({
      location: "/workspace/tools/recipes",
      text,
      uid: user?.uid ?? "",
    })
  }

  const { data, isLoading } = useGetHistoryTaskInfiniteQuery({
    variables: {
      includeFullDataImages: true,
      recipeIds: [sendRecipe],
      take: 20,
      search: debouncedSearch,
    },
    refetchOnWindowFocus: false,
    enabled: debouncedSearch !== "" && !loading,
  })

  const previewResult = useMemo(() => data?.pages.flatMap(i => i), [data])

  const buildPreviewResult = (search: string) => {
    if (isLoading) {
      return (
        <div className="p-1">
          <p className="text-atherGray-300 text-sm">Searching...</p>
        </div>
      )
    }

    if (!isLoading && previewResult?.length === 0) {
      return (
        <div className="p-1">
          <p className="text-atherGray-300 text-sm">No results found</p>
        </div>
      )
    }

    if (previewResult && previewResult.length > 0) {
      const resultLeft = previewResult.length

      return (
        <div className="flex flex-col space-y-2 overflow-y-auto overflow-x-hidden max-h-[55vh]">
          {previewResult?.map(item => (
            <div
              key={item.id}
              className="flex w-full rounded-2xl items-center hover:bg-atherGray-850 p-1 px-2 cursor-pointer"
            >
              <ImageWebpComponent
                className="w-12 h-12 rounded-lg object-cover"
                src={item.fullDataImages && item.fullDataImages?.length > 0 ? item.fullDataImages?.[0]?.url : ""}
                alt="avatar"
              />
              <div className="ml-2">
                <p className="text-sm line-clamp-1 font-semibold mb-1">{item.name}</p>
                <p className="text-xs truncate text-atherGray-300">
                  {user?.uid === item.creator?.uid ? `${item.creator?.name} (me)` : item.creator?.name}
                </p>
              </div>
            </div>
          ))}
          {resultLeft > 0 && (
            <button
              className="text-xs font-semibold text-atherGray-300 hover:text-atherGray-0 hover:underline text-left"
              onClick={() => handleConfirmSearch(search)}
            >
              ...and {resultLeft} other {resultLeft > 1 ? "results" : "result"}
            </button>
          )}
        </div>
      )
    }

    return null
  }

  return (
    <div
      className={cn("sticky top-0 md:top-[4.5rem] right-0 bg-black z-[1] w-full p-2 md:p-4 flex flex-col", className)}
    >
      <div className="flex items-center">
        <div className="flex flex-wrap justify-end flex-1">
          <SearchBar
            search={search}
            setSearch={setSearch}
            inputClassName="md:w-80"
            onConfirmSearch={handleConfirmSearch}
            history={history}
            previewResultBuilder={buildPreviewResult}
            onClear={() => {
              handleConfirmSearch?.("")
            }}
            onClearSearchHistory={text => {
              handleClearSearchHistory(text.text)
            }}
          />
          <button
            type="button"
            className={cn(
              "rounded-2xl ml-2 p-2 px-3 hover:bg-atherGray-850 flex items-center text-atherGray-300 transition-all",
              {
                "bg-atherGray-850 text-atherGray-0": selectionMode,
              },
            )}
            onClick={() => toggleSelectionMode()}
          >
            <CheckCircleIcon width={20} height={20} checked={selectionMode} />
            <span className="font-normal ml-1 text-sm hidden md:block">Multi-select</span>
          </button>
        </div>
      </div>
      {children}
    </div>
  )
}

export default RecipeSearch
